import type { ReactNode, Ref, ElementRef } from "react";
import * as ToggleGroupPrimitives from "@radix-ui/react-toggle-group";
import { cva, cx, type VariantProps } from "class-variance-authority";
import { Entries } from "../Icon/Entries";
import { Close } from "../Icon/Close";

import * as styles from "./ToggleGroup.module.scss";

type ToggleGroupRootProps = (
  | ToggleGroupPrimitives.ToggleGroupSingleProps
  | ToggleGroupPrimitives.ToggleGroupMultipleProps
) & {
  ref?: Ref<HTMLDivElement>;
};

const Root = ({ children, className, ref, ...props }: ToggleGroupRootProps) => {
  return (
    <ToggleGroupPrimitives.Root
      ref={ref}
      className={cx(styles.root, className)}
      {...props}
    >
      {children}
    </ToggleGroupPrimitives.Root>
  );
};
Root.displayName = "ToggleGroupRoot";

const toggleGroupItemVariants = cva("", {
  variants: {
    variant: {
      pill: styles.pill,
      chip: styles.chip,
      noStyle: "",
    },
  },
  defaultVariants: {
    variant: "chip",
  },
});

type ToggleGroupItemProps = ToggleGroupPrimitives.ToggleGroupItemProps &
  VariantProps<typeof toggleGroupItemVariants> & {
    ref?: Ref<ElementRef<typeof ToggleGroupPrimitives.Item>>;
  };

const Item = ({
  children,
  className,
  variant,
  ref,
  ...props
}: ToggleGroupItemProps) => {
  return (
    <ToggleGroupPrimitives.Item
      ref={ref}
      className={cx(toggleGroupItemVariants({ variant }), className)}
      {...props}
    >
      {children}
    </ToggleGroupPrimitives.Item>
  );
};
Item.displayName = "ToggleGroupItem";

type ClearToggleGroupButtonProps = {
  active: boolean;
  onClick: () => void;
  icon?: ReactNode;
  clearIcon?: ReactNode;
  className?: string;
};

// Sits alongside real ToggleGroupItems as a "clear all filters" pseudo-item —
// it isn't a real Radix toggle item. Radix's own pressed-state model has no
// way to represent "on" when nothing is selected (an empty/falsy value is
// never included in its tracked value array, in single or multiple mode), so
// this borrows the chip variant's class directly and drives data-state itself
// rather than rendering a ToggleGroupItem.
const ClearMultiple = ({
  active,
  onClick,
  icon,
  clearIcon,
  className,
}: ClearToggleGroupButtonProps) => {
  return (
    <button
      type="button"
      className={cx(
        toggleGroupItemVariants({ variant: "chip" }),
        styles.clearToggleGroupButton,
        className,
      )}
      data-state={active ? "on" : "off"}
      onClick={onClick}
    >
      {active
        ? icon ?? <Entries className={styles.icon} />
        : clearIcon ?? <Close className={styles.clearIcon} />}
    </button>
  );
};
ClearMultiple.displayName = "ToggleGroupClearMultiple";

const ToggleGroup = { Root, Item, ClearMultiple };

export {
  ToggleGroup,
  type ToggleGroupRootProps,
  type ToggleGroupItemProps,
  type ClearToggleGroupButtonProps,
};
